import to from 'await-to-js';
import React, { useEffect, useMemo, useState } from 'react';
import {
    alpha,
    Box,
    Button,
    ButtonBase,
    Card,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Bar,
    BarChart,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { differenceInDays, format, parseISO } from 'date-fns';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { PermissionName } from 'c-sdk';
import type { UsageOrganisationSort, UsageOrganisationType, UsageSummary } from 'c-sdk';
import { usageIcon } from 'c-main/icons';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { LoadingSpinner } from 'c-components';
import MuiRouterLink from 'c-components/MuiRouterLink';
import { NetworkRequestState } from 'c-data-layer';
import { formatNumberWhole } from 'c-lib';
import {
    PageStack,
    PeriodSelect,
    ShareBar,
    SortHeader,
    StaffToggle,
    tableHeadSx,
    TimeAgo,
    TrendBars,
    UsageEmpty,
    UsageHeader,
    UsageLoadError,
    UsageSection,
} from './UsageParts';
import type { TableSort } from './UsageParts';
import {
    DEFAULT_PERIOD_DAYS,
    organisationLink,
    percentOf,
    periodFor,
    useFormatDuration,
} from './usageLib';
import UsageActiveUsers from './UsageActiveUsers';
import { OrganisationsToWatch, WhatGotDone } from './UsageOverviewPanels';
import { pageLabel } from './activityLabels';
import {
    changeFor,
    OVERVIEW_METRICS,
    type OverviewMetric,
    previousLevel,
    seriesFor,
    totalFor,
} from './overviewMetrics';

const PER_PAGE = 25;
const TYPES: UsageOrganisationType[] = ['agency', 'owner', 'buyer', 'client'];
/** An organisation unseen for longer than this reads in red. */
const STALE_DAYS = 30;

/** Two cards side by side that share a height, so neither leaves a gap; they stack when narrow. */
const PairedRow = ({ children }: { children: [React.ReactNode, React.ReactNode] }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'stretch' }}>
        <Box sx={{ flex: '999 1 620px', minWidth: 0, display: 'flex', '& > *': { flex: 1 } }}>
            {children[0]}
        </Box>
        <Box sx={{ flex: '1 1 340px', minWidth: 0, display: 'flex', '& > *': { flex: 1 } }}>
            {children[1]}
        </Box>
    </Box>
);

/**
 * The headline numbers as one strip; each cell is also the chart's switch (board 1). Live
 * campaigns is a level on the last day, so it shows no change.
 */
const MetricStrip = ({
    summary,
    metric,
    onChange,
}: {
    summary: UsageSummary;
    metric: OverviewMetric;
    onChange: (metric: OverviewMetric) => void;
}) => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const { totals, previous_totals: previous } = summary;

    const valueOf = (key: OverviewMetric) => {
        if (key === 'time_ms') return formatDuration(totals.time_ms);
        if (key === 'active_organisations') {
            return t('Modules.Admin.Usage.kpi.activeOfTotal', {
                active: formatNumberWhole(totals.active_organisations),
                total: formatNumberWhole(totals.organisations),
            });
        }
        return formatNumberWhole(totalFor(totals, key));
    };

    return (
        <Card
            variant="outlined"
            role="group"
            aria-label={t('Modules.Admin.Usage.chartThis')}
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            }}
        >
            {OVERVIEW_METRICS.map(key => {
                const pressed = key === metric;
                const change = key === 'live_campaigns' ? null : changeFor(totals, previous, key);
                return (
                    <ButtonBase
                        key={key}
                        aria-pressed={pressed}
                        onClick={() => onChange(key)}
                        sx={{
                            px: 2.25,
                            pt: 1.5,
                            pb: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            textAlign: 'left',
                            borderRight: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            mr: '-1px',
                            mb: '-1px',
                            borderTop: 3,
                            borderTopColor: pressed ? 'primary.main' : 'transparent',
                            bgcolor: theme =>
                                pressed ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                            '&:hover': {
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                            },
                        }}
                    >
                        <Typography
                            variant="overline"
                            color={pressed ? 'primary.dark' : 'text.secondary'}
                            sx={{ lineHeight: 1.4, letterSpacing: '0.08em' }}
                        >
                            {t(`Modules.Admin.Usage.metrics.${key}`)}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 26,
                                fontWeight: 700,
                                lineHeight: 1.15,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {valueOf(key)}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {key === 'live_campaigns' ? (
                                t('Modules.Admin.Usage.liveOnLastDay')
                            ) : (
                                <>
                                    {change != null && (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontWeight: 600,
                                                color: change >= 0 ? 'success.dark' : 'error.main',
                                            }}
                                        >
                                            {change >= 0 ? '▲' : '▼'} {Math.abs(change)}%{' '}
                                        </Box>
                                    )}
                                    {change == null
                                        ? t('Modules.Admin.Usage.noPrevious')
                                        : t('Modules.Admin.Usage.vsPreviousShort')}
                                </>
                            )}
                        </Typography>
                    </ButtonBase>
                );
            })}
        </Card>
    );
};

/**
 * The chosen number per day: weekends lighter, the previous period as a dashed line. It grows with
 * the column beside it, but only to 340px: past that a month of bars reads as a wall.
 */
const MetricChart = ({ summary, metric }: { summary: UsageSummary; metric: OverviewMetric }) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const points = useMemo(() => seriesFor(summary.daily, metric), [summary, metric]);
    const line = previousLevel(summary.previous_average, metric);
    const title = t('Modules.Admin.Usage.perDay', {
        metric: t(`Modules.Admin.Usage.metrics.${metric}`),
    });
    const weekend = alpha(theme.palette.primary.main, 0.35);
    const formatValue = (value: number) =>
        metric === 'time_ms' ? `${value.toFixed(1)}h` : formatNumberWhole(value);
    const empty = points.every(point => point.value === 0);

    const key = (
        <Box
            aria-hidden="true"
            sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap', alignItems: 'center' }}
        >
            {[
                [theme.palette.primary.main, t('Modules.Admin.Usage.weekday')],
                [weekend, t('Modules.Admin.Usage.weekend')],
            ].map(([colour, label]) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: colour }} />
                    <Typography variant="caption" color="text.secondary">
                        {label}
                    </Typography>
                </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                    sx={{
                        width: 14,
                        borderTop: 2,
                        borderStyle: 'dashed',
                        borderColor: 'text.disabled',
                    }}
                />
                <Typography variant="caption" color="text.secondary">
                    {t('Modules.Admin.Usage.previousPeriod')}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <UsageSection title={title} hint={t('Modules.Admin.Usage.chartHint')} aside={key}>
            {empty ? (
                <UsageEmpty />
            ) : (
                <Box
                    role="img"
                    aria-label={title}
                    sx={{ flex: 1, minHeight: 240, maxHeight: 340, display: 'flex' }}
                >
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                        minHeight={240}
                        initialDimension={{ width: 600, height: 240 }}
                    >
                        <BarChart data={points} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                            <XAxis
                                dataKey="date"
                                tickFormatter={(date: string) => format(parseISO(date), 'd MMM')}
                                tickLine={false}
                                axisLine={{ stroke: theme.palette.divider }}
                                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                                minTickGap={24}
                            />
                            <YAxis
                                allowDecimals={metric === 'time_ms'}
                                width={40}
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 11, fill: theme.palette.text.disabled }}
                            />
                            <Tooltip
                                cursor={{ fill: alpha(theme.palette.primary.main, 0.06) }}
                                labelFormatter={(date: string) =>
                                    format(parseISO(date), 'EEE d MMM yyyy')
                                }
                                formatter={(value: number) => [formatValue(value), title]}
                            />
                            <Bar dataKey="value" name={title} radius={[3, 3, 0, 0]} maxBarSize={28}>
                                {points.map(point => (
                                    <Cell
                                        key={point.date}
                                        fill={point.weekend ? weekend : theme.palette.primary.main}
                                    />
                                ))}
                            </Bar>
                            {line > 0 && (
                                <ReferenceLine
                                    y={line}
                                    stroke={theme.palette.text.disabled}
                                    strokeDasharray="5 4"
                                    strokeWidth={1.5}
                                />
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </UsageSection>
    );
};

const isStale = (lastActive: string | null) =>
    !lastActive || differenceInDays(new Date(), parseISO(lastActive)) > STALE_DAYS;

const UsageOverviewPage = () => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const [days, setDays] = useState(DEFAULT_PERIOD_DAYS);
    const [metric, setMetric] = useState<OverviewMetric>('active_users');
    const [type, setType] = useState<UsageOrganisationType | ''>('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    // Null: the API's own order, most active first.
    const [sort, setSort] = useState<TableSort<UsageOrganisationSort> | null>(null);
    const [includeStaff, setIncludeStaff] = useState(false);
    const period = useMemo(
        () => ({ ...periodFor(days), include_staff: includeStaff ? (1 as const) : (0 as const) }),
        [days, includeStaff],
    );

    const summaryRequest = useAPIClientRequest(ApiClient.Usage.summary);
    const orgRequest = useAPIClientRequest(ApiClient.Usage.organisations);
    const { start: startSummary } = summaryRequest;
    const { start: startOrgs } = orgRequest;

    useEffect(() => {
        to(startSummary(period));
    }, [period, startSummary]);

    useEffect(() => {
        to(
            startOrgs({
                ...period,
                type: type === '' ? undefined : type,
                search: search === '' ? undefined : search,
                page,
                per_page: PER_PAGE,
                sort: sort?.key,
                direction: sort?.direction,
            }),
        );
    }, [period, type, search, page, sort, startOrgs]);

    const sortOrgs = (next: TableSort<UsageOrganisationSort>) => {
        setSort(next);
        setPage(1);
    };

    const summary = summaryRequest.data?.data?.data;
    const orgs = orgRequest.data?.data;
    const right = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

    return (
        <PageStack>
            <UsageHeader
                title={t('Modules.Admin.Usage.metaTitle')}
                subtitle={t('Modules.Admin.Usage.subtitle')}
                controls={
                    <>
                        <StaffToggle
                            checked={includeStaff}
                            onChange={value => {
                                setIncludeStaff(value);
                                setPage(1);
                            }}
                        />
                        <PeriodSelect
                            value={days}
                            onChange={value => {
                                setDays(value);
                                setPage(1);
                            }}
                        />
                        <Button
                            component={MuiRouterLink}
                            to={PostAuthRoutes.Admin.UsageFeatures}
                            variant="outlined"
                        >
                            {t('Modules.Admin.Usage.featuresTitle')}
                        </Button>
                    </>
                }
            />

            {summaryRequest.requestState === NetworkRequestState.Error && <UsageLoadError />}
            {summaryRequest.requestState === NetworkRequestState.InProgress && !summary && (
                <LoadingSpinner />
            )}

            {summary && (
                <>
                    <MetricStrip summary={summary} metric={metric} onChange={setMetric} />
                    <PairedRow>
                        <MetricChart summary={summary} metric={metric} />
                        <OrganisationsToWatch watch={summary.organisations_to_watch} />
                    </PairedRow>
                    <PairedRow>
                        <UsageActiveUsers users={summary.users} />
                        <WhatGotDone summary={summary} />
                    </PairedRow>
                </>
            )}

            <UsageSection
                title={t('Modules.Admin.Usage.organisations')}
                hint={t('Modules.Admin.Usage.organisationsHint')}
                aside={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="usage-type-label">
                                {t('Modules.Admin.Usage.type')}
                            </InputLabel>
                            <Select
                                labelId="usage-type-label"
                                label={t('Modules.Admin.Usage.type')}
                                value={type}
                                onChange={event => {
                                    setType(event.target.value as UsageOrganisationType | '');
                                    setPage(1);
                                }}
                            >
                                <MenuItem value="">{t('Modules.Admin.Usage.allTypes')}</MenuItem>
                                {TYPES.map(value => (
                                    <MenuItem key={value} value={value}>
                                        {t(`Modules.Admin.Usage.types.${value}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            type="search"
                            label={t('Modules.Admin.Usage.search')}
                            placeholder={t('Modules.Admin.Usage.searchPlaceholder')}
                            value={search}
                            onChange={event => {
                                setSearch(event.target.value);
                                setPage(1);
                            }}
                        />
                    </Box>
                }
            >
                {orgRequest.requestState === NetworkRequestState.Error && <UsageLoadError />}
                {orgs && orgs.data.length === 0 && <UsageEmpty />}
                {orgs && orgs.data.length > 0 && (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={tableHeadSx}>
                                    <TableRow>
                                        <SortHeader
                                            column="name"
                                            sort={sort}
                                            onSort={sortOrgs}
                                            first="asc"
                                        >
                                            {t('Modules.Admin.Usage.columns.organisation')}
                                        </SortHeader>
                                        <SortHeader
                                            column="type"
                                            sort={sort}
                                            onSort={sortOrgs}
                                            first="asc"
                                        >
                                            {t('Modules.Admin.Usage.columns.type')}
                                        </SortHeader>
                                        <SortHeader
                                            column="active_users"
                                            sort={sort}
                                            onSort={sortOrgs}
                                        >
                                            {t('Modules.Admin.Usage.columns.activePeople')}
                                        </SortHeader>
                                        <SortHeader
                                            column="sessions"
                                            sort={sort}
                                            onSort={sortOrgs}
                                            align="right"
                                        >
                                            {t('Modules.Admin.Usage.columns.sessions')}
                                        </SortHeader>
                                        <SortHeader
                                            column="time_ms"
                                            sort={sort}
                                            onSort={sortOrgs}
                                            align="right"
                                        >
                                            {t('Modules.Admin.Usage.columns.time')}
                                        </SortHeader>
                                        <TableCell>
                                            {t('Modules.Admin.Usage.columns.mostUsed')}
                                        </TableCell>
                                        <SortHeader
                                            column="last_active"
                                            sort={sort}
                                            onSort={sortOrgs}
                                        >
                                            {t('Modules.Admin.Usage.columns.lastActive')}
                                        </SortHeader>
                                        <TableCell>
                                            {t('Modules.Admin.Usage.columns.trend')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orgs.data.map(org => (
                                        <TableRow key={`${org.type}-${org.id}`}>
                                            <TableCell>
                                                <MuiRouterLink
                                                    to={organisationLink(org.type, org.id)}
                                                    fontWeight={500}
                                                    underline="hover"
                                                >
                                                    {org.name}
                                                </MuiRouterLink>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        color: 'primary.dark',
                                                        bgcolor: theme =>
                                                            alpha(theme.palette.primary.main, 0.1),
                                                        borderRadius: 999,
                                                        px: 1.1,
                                                        py: '3px',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {t(`Modules.Admin.Usage.types.${org.type}`)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.25,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            minWidth: 56,
                                                            fontVariantNumeric: 'tabular-nums',
                                                        }}
                                                    >
                                                        {t(
                                                            'Modules.Admin.Usage.kpi.activeOfTotal',
                                                            {
                                                                active: org.active_users,
                                                                total: org.total_users,
                                                            },
                                                        )}
                                                    </Typography>
                                                    <ShareBar
                                                        percent={percentOf(
                                                            org.active_users,
                                                            org.total_users,
                                                        )}
                                                        label={t(
                                                            'Modules.Admin.Usage.columns.activePeople',
                                                        )}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={right}>
                                                {formatNumberWhole(org.sessions)}
                                            </TableCell>
                                            <TableCell sx={right}>
                                                {formatDuration(org.time_ms)}
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary' }}>
                                                {org.top_page
                                                    ? pageLabel(org.top_page)
                                                    : t('Modules.Admin.Usage.duration.none')}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    color: isStale(org.last_active)
                                                        ? 'error.main'
                                                        : 'text.primary',
                                                }}
                                            >
                                                {org.last_active ? (
                                                    <TimeAgo date={org.last_active} />
                                                ) : (
                                                    t('Modules.Admin.Usage.never')
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TrendBars points={org.trend} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {t('Modules.Admin.Usage.showingOf', {
                                    shown: orgs.data.length,
                                    total: orgs.meta.total,
                                })}
                            </Typography>
                            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                                {t('Modules.Admin.Usage.previous')}
                            </Button>
                            <Button
                                disabled={page * PER_PAGE >= orgs.meta.total}
                                onClick={() => setPage(page + 1)}
                            >
                                {t('Modules.Admin.Usage.next')}
                            </Button>
                        </Box>
                    </>
                )}
            </UsageSection>
        </PageStack>
    );
};

const setup: PageConfig = {
    id: 'UsageOverviewPage',
    component: UsageOverviewPage,
    systemSearch: {
        title: 'Modules.Admin.Usage.metaTitle',
        description: 'Modules.Admin.Usage.description',
        type: 'page',
        saveAsRecentPage: true,
        aliases: undefined,
        icon: usageIcon,
        route: PostAuthRoutes.Admin.UsageOverview,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            { label: 'Modules.Admin.Usage.metaTitle' },
        ],
        permissions: [PermissionName.AdminCan_view_usage],
    },
};

export default setup;
